import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import CategoryHeader from '../components/category/CategoryHeader';
import CategoryControls from '../components/category/CategoryControls';
import BusinessGrid from '../components/category/BusinessGrid';
import { getBusinessesByCategory } from '../data/businesses';
import { getServiceBySlug } from '../data/categoryConfig';
import './CategoryPageRefactored.css';

// Shared category data - could be moved to constants file
const CATEGORIES = {
  'restaurants': {
    name: 'Restaurants',
    description: 'Find best restaurants, cafes, and dining options in your area',
    icon: '🍽️',
    color: '#ff6b6b'
  },
  'hotels': {
    name: 'Hotels',
    description: 'Book comfortable hotels and accommodations for your stay',
    icon: '🏨',
    color: '#4ecdc4'
  },
  'beauty-spa': {
    name: 'Beauty Spa',
    description: 'Discover beauty salons, spas, and wellness centers',
    icon: '💅',
    color: '#f7b731'
  },
  'home-decor': {
    name: 'Home Decor',
    description: 'Find home decor services and interior designers',
    icon: '🏠',
    color: '#5f27cd'
  },
  'wedding-planning': {
    name: 'Wedding Planning',
    description: 'Plan your perfect wedding with expert planners',
    icon: '💒',
    color: '#e74c3c'
  },
  'education': {
    name: 'Education',
    description: 'Find educational institutions and learning centers',
    icon: '📚',
    color: '#3498db'
  },
  'rent-hire': {
    name: 'Rent & Hire',
    description: 'Rent equipment, vehicles, and services on demand',
    icon: '🔧',
    color: '#2ecc71'
  },
  'hospitals': {
    name: 'Hospitals',
    description: 'Find hospitals and healthcare facilities',
    icon: '🏥',
    color: '#e67e22'
  },
  'contractors': {
    name: 'Contractors',
    description: 'Connect with reliable contractors for your projects',
    icon: '👷',
    color: '#95a5a6'
  },
  'pet-shops': {
    name: 'Pet Shops',
    description: 'Find pet supplies, grooming, and veterinary services',
    icon: '🐕',
    color: '#f39c12'
  },
  'pg-hostels': {
    name: 'PG/Hostels',
    description: 'Find paying guest accommodations and hostels',
    icon: '🏘️',
    color: '#9b59b6'
  },
  'dentists': {
    name: 'Dentists',
    description: 'Find dental clinics and oral healthcare services',
    icon: '🦷',
    color: '#1abc9c'
  },
  'gym': {
    name: 'Gym',
    description: 'Find fitness centers and gym facilities',
    icon: '💪',
    color: '#e74c3c'
  },
  'event-organisers': {
    name: 'Event Organisers',
    description: 'Plan events with professional organizers',
    icon: '🎉',
    color: '#f39c12'
  },
  'events': {
    name: 'Events',
    description: 'Find professional event planning and management services',
    icon: '🎉',
    color: '#f39c12'
  },
  'villa': {
    name: 'Villa',
    description: 'Luxury villa rentals for holidays and getaways',
    icon: '🏡',
    color: '#27ae60'
  },
  'ac-service': {
    name: 'AC Service',
    description: 'Professional AC installation, repair and maintenance',
    icon: '❄️',
    color: '#3498db'
  },
  'physiotherapy': {
    name: 'Physiotherapy',
    description: 'Find physiotherapy and rehabilitation services',
    icon: '🏥',
    color: '#e67e22'
  },
  'driving-schools': {
    name: 'Driving Schools',
    description: 'Learn driving from certified instructors',
    icon: '🚗',
    color: '#34495e'
  },
  'packers-movers': {
    name: 'Packers & Movers',
    description: 'Relocate with trusted packing and moving services',
    icon: '📦',
    color: '#16a085'
  },
  'courier-service': {
    name: 'Courier Service',
    description: 'Fast and reliable courier delivery services',
    icon: '📬',
    color: '#27ae60'
  },
  'popular': {
    name: 'Popular Categories',
    description: 'Browse trending and popular business categories',
    icon: '⭐',
    color: '#f1c40f'
  }
};

const CategoryPageRefactored = ({ categorySlug }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [prefillSearchQuery, setPrefillSearchQuery] = useState('');
  const [isCategoryRefineSearchActive, setIsCategoryRefineSearchActive] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({});

  const category = CATEGORIES[categorySlug];
  const service = useMemo(() => getServiceBySlug(categorySlug), [categorySlug]);

  // Parse query parameters from URL
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      q: params.get('q') || ''
    };
  };

  // Read search query from URL params on mount
  useEffect(() => {
    const { q } = getQueryParams();
    if (q) {
      // Home/dashboard search is category-intent based.
      // We prefill the input but DO NOT text-filter businesses until user refines inside this category page.
      setPrefillSearchQuery(q);
      setSearchQuery('');
      setIsCategoryRefineSearchActive(false);
    } else {
      setPrefillSearchQuery('');
      setSearchQuery('');
      setIsCategoryRefineSearchActive(false);
    }
  }, [location.search]);

  // Calculate stats
  const stats = useMemo(() => ({
    businessCount: businesses.length,
    avgRating: businesses.length > 0 
      ? (businesses.reduce((sum, b) => sum + b.rating, 0) / businesses.length).toFixed(1)
      : '0.0',
    verifiedCount: businesses.filter(b => b.verified).length
  }), [businesses]);

  // Load businesses by category on mount
  useEffect(() => {
    const loadBusinesses = async () => {
      setLoading(true);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Get businesses for this specific category
      const categoryBusinesses = getBusinessesByCategory(categorySlug);
      setBusinesses(categoryBusinesses);
      setLoading(false);
    };

    loadBusinesses();
  }, [categorySlug]);

  // Apply filters and search
  useEffect(() => {
    let filtered = businesses;

    // Apply search filter
    if (isCategoryRefineSearchActive && searchQuery) {
      filtered = filtered.filter(business =>
        business.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply price filter
    if (filters.price && filters.price.length > 0) {
      filtered = filtered.filter(business => 
        filters.price.includes(business.priceRange)
      );
    }

    // Apply features filter
    if (filters.features && filters.features.length > 0) {
      filtered = filtered.filter(business => {
        const businessFeatures = [];
        if (business.delivery) businessFeatures.push('delivery');
        if (business.takeaway) businessFeatures.push('takeaway');
        if (business.reservation) businessFeatures.push('reservation');
        if (business.features) businessFeatures.push(...business.features.map(f => f.toLowerCase()));
        
        return filters.features.some(filter => 
          businessFeatures.includes(filter)
        );
      });
    }

    // Apply rating filter
    if (filters.rating) {
      filtered = filtered.filter(business => business.rating >= filters.rating);
    }

    // Apply verified filter
    if (filters.verified) {
      filtered = filtered.filter(business => business.verified);
    }

    // Apply open now filter (mock logic - in real app, check actual hours)
    if (filters.openNow) {
      filtered = filtered.filter(business => {
        const now = new Date();
        const currentHour = now.getHours();
        const [openHour] = business.hours.match(/(\d+):\d+\s*(AM|PM)/i) || [];
        if (openHour) {
          const hour = parseInt(openHour);
          const period = business.hours.includes('PM') && hour < 12 ? hour + 12 : hour;
          return currentHour >= period && currentHour < period + 8; // Assume 8 hours business day
        }
        return true;
      });
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'reviews':
          return b.reviews - a.reviews;
        case 'newest':
          return b.id - a.id;
        case 'distance':
          // Mock distance calculation - in real app, use geolocation
          return Math.random() - 0.5;
        default:
          return 0;
      }
    });

    setFilteredBusinesses(filtered);
  }, [businesses, isCategoryRefineSearchActive, searchQuery, sortBy, filters]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setIsCategoryRefineSearchActive(true);
  };

  const handleSort = (sort) => {
    setSortBy(sort);
  };

  const handleFilter = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleContact = (type, value, business) => {
    switch (type) {
      case 'call':
        window.open(`tel:${value}`);
        break;
      case 'email':
        window.open(`mailto:${value}`);
        break;
      case 'website':
        window.open(`https://${value}`, '_blank');
        break;
      default:
        console.log('Contact:', type, value, business);
    }
  };

  const handleViewDetails = (business) => {
    console.log('View details:', business);
    // Navigate to business details page
    // history.push(`/business/${business.id}`);
  };

  return (
    <div className="category-page-refactored">
      {category ? (
        <CategoryHeader 
          category={category} 
          stats={stats}
        />
      ) : null}
      
      <CategoryControls
        onSearch={handleSearch}
        onSort={handleSort}
        onFilter={handleFilter}
        businessCount={stats.businessCount}
        initialSearchQuery={prefillSearchQuery}
      />

      <BusinessGrid
        businesses={filteredBusinesses}
        loading={loading}
        onContact={handleContact}
        onViewDetails={handleViewDetails}
        categorySlug={categorySlug}
      />

      {/* Call to Action */}
      {category || service ? (
        <section className="category-cta">
          <div className="cta-content">
            <h2>List Your {(category && category.name) || (service && service.name)} Business</h2>
            <p>Join thousands of businesses and reach more customers</p>
            <a href="/business-listing" className="cta-button">
              List Your Business
            </a>
          </div>
        </section>
      ) : null}
    </div>
  );
};

CategoryPageRefactored.propTypes = {
  categorySlug: PropTypes.string.isRequired
};

export default CategoryPageRefactored;
